import { useState } from 'react';
import type { MemberCeiling } from '@/lib/clients/vault/orgs';

/**
 * The memory bank's ONE empty state — the honest half of the gate. A panel
 * with zero rows is either "nothing matches" or "everything that matches is
 * outside your view", and the SERVER makes the two indistinguishable on
 * purpose (a hidden record must read exactly like an absent one — the same
 * 404=403 doctrine the reads follow). So this component never says which it
 * is, never counts what is hidden, and never names a hidden record: the one
 * line it renders is the same for both causes.
 *
 * The line is a dead end on its own, so the component carries the second
 * half: an EXPANDABLE access explainer — "this view is filtered by your
 * access" plus the viewer's own scope grants and privacy ceiling, i.e. the
 * two factors the server ANDs into the gate. Non-attributed by construction:
 * it describes the VIEWER's credentials only, never what those credentials
 * exclude.
 */

/** One of the viewer's own scope grants, flattened for ordered rendering. */
export interface MemoryAccessGrant {
  name: string;
  level: string;
}

/**
 * What the explainer knows about the viewer's gate. `viewer: 'owner'` means
 * the org vault is the wallet's own vault — key math, no grant rows and no
 * ceiling above them (the same rule `fetchOrgs` renders the owner entry
 * under). `null` access (the read is still in flight, was refused, or could
 * not attribute anything) still explains the MODEL, just without specifics.
 */
export interface MemoryAccess {
  viewer: 'owner' | 'member';
  grants: MemoryAccessGrant[];
  /** The viewer's signed privacy ceiling, or its honest unknowns
   *  (`'none'` = v1 document; `'unknown'` = not readable/attributable). */
  ceiling: MemberCeiling;
}

const CEILING_TEXT: Record<MemberCeiling, string> = {
  public: 'public',
  internal: 'internal',
  private: 'private',
  restricted: 'restricted',
  none: 'none signed',
  unknown: 'unknown',
};

export function MemoryEmptyState({ access }: { access: MemoryAccess | null }) {
  const [explained, setExplained] = useState(false);
  return (
    <div className="vault-orgs__memory-zero" data-testid="memory-zero">
      <p className="vault-orgs__memory-zero-line">
        No rows — a hidden record is indistinguishable from an absent one
      </p>
      <button
        type="button"
        className="vault-orgs__memory-zero-toggle"
        aria-expanded={explained}
        data-testid="memory-zero-toggle"
        onClick={() => setExplained((open) => !open)}
      >
        {explained ? 'Hide how access shapes this view' : 'How access shapes this view'}
      </button>
      {explained && (
        <div className="vault-orgs__memory-access" data-testid="memory-access">
          <p>This view is filtered by your access.</p>
          {access === null ? (
            <p className="vault-orgs__memory-access-detail">
              Your grants and privacy ceiling for this vault could not be read right now.
            </p>
          ) : access.viewer === 'owner' ? (
            <p className="vault-orgs__memory-access-detail">
              You own this vault — your gate is the whole bank, with no ceiling above you.
            </p>
          ) : (
            <>
              <p className="vault-orgs__memory-access-detail">
                Your scope grants
                {access.grants.length === 0
                  ? ': none'
                  : `: ${access.grants.map((g) => `${g.name} · ${g.level}`).join(', ')}`}
              </p>
              <p className="vault-orgs__memory-access-detail">
                {`Your privacy ceiling: ${CEILING_TEXT[access.ceiling]}`}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
